import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { catchError, defaultIfEmpty, map, of } from 'rxjs';
import { ExtranetService } from '../services/extranet.service';
import { startSignIn } from '../auth/start-sign-in';
import { isAuthBypassed } from '../auth/auth-bypass';

// Deliberately a SEPARATE guard, mirroring adminGuard rather than widening hotelManagerGuard:
// the Clients pages are agency-only surfaces (client ledger, debt, payments), meaningless for a
// hotel group and for a pure platform admin. Keeping the guards disjoint means every existing
// route keeps behaving exactly as before, with zero risk from this addition.
//
// Same resolution path as the other guards: GET /api/extranet/access/me, never a B2C token
// claim. Not signed in -> B2C sign-in redirect. Signed in but role !== AGENCY_MANAGER ->
// redirect Home (per the clients handoff: hotel managers and admins reaching the route by URL
// land on the dashboard, not on /unauthorized).
export const agencyManagerGuard: CanActivateFn = (_route, state) => {
  const msal = inject(MsalService);
  const router = inject(Router);
  const extranetSvc = inject(ExtranetService);

  // Locally there is no MSAL account to find — the interceptor authenticates with an unsigned dev
  // JWT instead — so only the sign-in step is skipped. Role resolution below still runs against the
  // real server, exactly as in production.
  if (!isAuthBypassed()) {
    const account = msal.instance.getActiveAccount();
    // Not signed in: B2C sign-in, then back to this very page (WI 3861) — see start-sign-in.ts.
    if (!account) return startSignIn(msal, router, state.url);
  }

  return extranetSvc.getMe().pipe(
    map((me) => {
      if (me.role === 'AGENCY_MANAGER') return true;
      router.navigate(['/extranet/dashboard']);
      return false;
    }),
    catchError(() => {
      router.navigate(['/unauthorized']);
      return of(false);
    }),
    // GET /me completes without a value while the interceptor re-authenticates an expired
    // session by redirect (WI 3861): cancel this navigation, the redirect takes the page over.
    defaultIfEmpty(false),
  );
};
